import React, { useEffect, useState } from 'react';
import './App.css';
import Table from './components/Table';
import AddEmployeeModal from './components/AddEmployeeModal';
import ArchiveModal from './components/ArchiveModal';
import Login from './components/Login';
import { Employee } from './types';

type SortField = '' | 'fullName' | 'position' | 'passNumber' | 'expirationDate';
type SortOrder = 'asc' | 'desc';

const API_URL = '/employees';
const ARCHIVE_URL = '/archive';

const App: React.FC = () => {
  const [isAuthenticated, setAuthenticated] = useState(localStorage.getItem('auth') === 'true');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [archived, setArchived] = useState<Employee[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isArchiveOpen, setArchiveOpen] = useState(false);
  const [isStatsOpen, setStatsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<SortField>('fullName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('ru-RU');
  const daysLeft = (dateStr: string) => Math.floor((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  const fetchArchive = async () => {
    const res = await fetch(ARCHIVE_URL);
    const data = await res.json();
    setArchived(data);
  };

  useEffect(() => {
    const authTime = localStorage.getItem('authTime');
    if (authTime) {
      const expired = Date.now() - Number(authTime) > 30 * 60 * 1000;
      if (expired) {
        localStorage.removeItem('auth');
        localStorage.removeItem('authTime');
        setAuthenticated(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetch(API_URL)
        .then(res => res.json())
        .then(data => setEmployees(data));

      fetchArchive();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Login onLogin={() => setAuthenticated(true)} />;
  }

  const warningList = employees.filter(e => daysLeft(e.expirationDate) < 60 && daysLeft(e.expirationDate) >= 30);
  const criticalList = employees.filter(e => daysLeft(e.expirationDate) < 30);

  const filteredEmployees = employees.filter(emp =>
    emp.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.position.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.passNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.expirationDate.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField].toLowerCase?.() || a[sortField];
    const bVal = b[sortField].toLowerCase?.() || b[sortField];
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedEmployees.length / pageSize);
  const paginatedEmployees = sortedEmployees.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const addEmployee = async (emp: Omit<Employee, 'id'>) => {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emp),
    });
    const savedEmp = await res.json();
    setEmployees(prev => [savedEmp, ...prev]);
    setCurrentPage(1);
  };

  const updateEmployee = async (updated: Employee) => {
    await fetch(`${API_URL}/${updated.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    });
    setEmployees(prev => prev.map(emp => emp.id === updated.id ? updated : emp));
    setEditingEmployee(null);
  };

  const deleteEmployee = async (target: Employee) => {
    await fetch(`${API_URL}/${target.id}`, { method: 'DELETE' });
    setEmployees(prev => prev.filter(emp => emp.id !== target.id));
    await fetchArchive(); // обновим архив после удаления
  };
  const exportToCSV = () => {
    const header = ['ФИО', 'Должность', 'Номер пропуска', 'Дата окончания'];
    const rows = employees.map(emp => [
      emp.fullName,
      emp.position,
      emp.passNumber,
      formatDate(emp.expirationDate)
    ]);
    const csvContent = [header, ...rows].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'сотрудники.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const restoreArchived = async (ids: string[]) => {
    const res = await fetch('/archive/restore', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }) // Передаем массив ID
    });
  
    if (res.ok) {
      const restored = await res.json();
      setEmployees(prev => [...restored, ...prev]);  // Добавляем восстановленных сотрудников в общий список
      setArchived(prev => prev.filter(emp => !ids.includes(emp.id)));
    } else {
      console.error("Ошибка восстановления сотрудников");
    }
  };

  return (
    <div className="app-container">
      <div style={{ textAlign: 'right', marginBottom: '10px' }}>
        <button className="secondary-btn" onClick={() => {
          localStorage.removeItem('auth');
          localStorage.removeItem('authTime');
          setAuthenticated(false);
        }}>Выйти</button>
      </div>

      <div className="logo-block">
        <img src="/logo.png" alt="Логотип" className="logo" />
        <h1>Контроль срока действия пропусков</h1>
        <h1>на аэродром «Красноярск»</h1>
        <h1>для сотрудников АДЦ ЕС ОрВД (Красноярск)</h1>
        <br /><br />
      </div>

      <div className="header-row">
        <div className="left-buttons">
          <button onClick={() => setModalOpen(true)}>Добавить сотрудника</button>
          <button onClick={() => setMenuOpen(true)} className="secondary-btn">Меню</button>
        </div>
      </div>

      {menuOpen && (
        <div className="modal-overlay">
          <div className="modal-window">
            <h3 style={{ textAlign: 'center' }}>Меню</h3>
            <div className="menu-buttons">
              <button onClick={() => { setMenuOpen(false); setStatsOpen(true); }}>Статистика</button>
              <button onClick={() => { setMenuOpen(false); exportToCSV(); }}>Экспорт данных</button>
              <button onClick={() => { setMenuOpen(false); setArchiveOpen(true); }}>Архив</button>
              <button onClick={() => { setMenuOpen(false); alert('Функция Email уведомлений пока не реализована'); }}>Email уведомление</button>
            </div>
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button onClick={() => setMenuOpen(false)} className="secondary-btn">Закрыть</button>
            </div>
          </div>
        </div>
      )}

      <div className="search-row">
        <input
          type="text"
          placeholder="Поиск..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {criticalList.length > 0 && (
        <div className="alert critical">
          <strong>Обнаружены критические сроки:</strong>
          <ul>
            {criticalList.map((emp, idx) => (
              <li key={idx}>
                <span className="link" onClick={() => setEditingEmployee(emp)}>{emp.fullName} </span>
                ({formatDate(emp.expirationDate)}, дней: {daysLeft(emp.expirationDate)})
              </li>
            ))}
          </ul>
        </div>
      )}

      {warningList.length > 0 && (
        <div className="alert warning">
          <strong>Обнаружены подходящие сроки:</strong>
          <ul>
            {warningList.map((emp, idx) => (
              <li key={idx}>
                <span className="link" onClick={() => setEditingEmployee(emp)}>{emp.fullName} </span>
                ({formatDate(emp.expirationDate)}, дней: {daysLeft(emp.expirationDate)})
              </li>
            ))}
          </ul>
        </div>
      )}

      {criticalList.length === 0 && warningList.length === 0 && (
        <div className="alert ok">✅ Все сроки в норме.</div>
      )}

      <Table
        employees={paginatedEmployees}
        onEdit={setEditingEmployee}
        onDelete={setEmployeeToDelete}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(field) => {
          setSortField(field as SortField);
          setSortOrder(prev => (field === sortField ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
        }}
      />

      {totalPages > 1 && (
        <div className="pagination">
          <div>
            <label>Строк на странице: </label>
            <select value={pageSize} onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}>
              {[10, 20, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>←</button>
            <span style={{ margin: '0 8px' }}>
              Страница {currentPage} из {totalPages}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>→</button>
          </div>
        </div>
      )}

      <div className="footer-note">Разработал Мельничук Е.Н.</div>

      {isModalOpen && (
        <AddEmployeeModal
          onClose={() => setModalOpen(false)}
          onSave={(emp) => {
            addEmployee(emp);
            setModalOpen(false);
          }}
        />
      )}

      {editingEmployee && (
        <AddEmployeeModal
          initialData={editingEmployee}
          onClose={() => setEditingEmployee(null)}
          onSave={(emp) => updateEmployee(emp as Employee)}
        />
      )}

      {employeeToDelete && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2>Удалить сотрудника</h2>
            <p>Вы уверены, что хотите удалить <strong>{employeeToDelete.fullName}</strong>?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                style={{ backgroundColor: '#dc3545' }}
                onClick={() => {
                  deleteEmployee(employeeToDelete);
                  setEmployeeToDelete(null);
                }}
              >Удалить</button>
              <button onClick={() => setEmployeeToDelete(null)} className="secondary-btn">Отмена</button>
            </div>
          </div>
        </div>
      )}

      {isArchiveOpen && (
        <ArchiveModal
        archived={archived.filter((emp): emp is Employee & { archivedAt: string } => 'archivedAt' in emp)}
        onRestore={restoreArchived}
        onClose={() => setArchiveOpen(false)}
        />
      )}

      {isStatsOpen && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>Статистика внесенных работников</h3>
            <ul>
  <li>РП АДЦ: {employees.filter((e) => e.position === 'РП АДЦ').length}</li>
  <li>Старший диспетчер АДЦ: {employees.filter((e) => e.position === 'Старший диспетчер АДЦ').length}</li>
  <li>Диспетчер ОН УВД АДЦ: {employees.filter((e) => e.position === 'Диспетчер ОН УВД АДЦ').length}</li>
  <li>Внесено всего: {employees.length}</li>
  <li>
    Заканчивается срок действия в текущем году: {
      employees.filter((e) =>
        new Date(e.expirationDate).getFullYear() === new Date().getFullYear()
      ).length
    }
    <details style={{ marginTop: '8px', marginLeft: '16px' }}>
  <summary>Помесячно</summary>
  <ul>
    {[...Array(12 - new Date().getMonth())].map((_, idx) => {
      const month = new Date().getMonth() + idx;
      const employeesInMonth = employees.filter((e) => {
        const date = new Date(e.expirationDate);
        return (
          date.getFullYear() === new Date().getFullYear() &&
          date.getMonth() === month
        );
      });

      const count = employeesInMonth.length;
      const monthName = new Date(0, month).toLocaleString('ru-RU', {
        month: 'long',
      });

      return (
        <li key={month} style={{ marginBottom: '6px' }}>
          <details>
            <summary>{monthName}: {count}</summary>
            {employeesInMonth.length > 0 ? (
              <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                {employeesInMonth.map((e, idx) => (
                  <li key={idx}>
                    {e.fullName} — {new Date(e.expirationDate).toLocaleDateString('ru-RU')}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#999', marginLeft: '10px' }}>Нет записей</p>
            )}
          </details>
        </li>
      );
    })}
  </ul>
</details>
  </li>
</ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setStatsOpen(false)} className="secondary-btn">Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
